import { Button, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataModelConfiguration } from "../../../../api/types/dataModelConfigurationTypes";
import { useConfigurationsGridActionsContext } from "./ConfigurationsGridActionsContext";
import { getInitialEditConfigurationState, isFormDirty, updateNameAction } from "./editConfigurationState";

interface Props {
  onClose: () => void;
  onCreated: (newConfiguration: DataModelConfiguration) => void;
}

const createConfiguration = withErrorHandling(adminApi.createDataModelConfiguration);

const CreateConfigurationDialog = ({ onClose, onCreated }: Props) => {
  const { configurations } = useConfigurationsGridActionsContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(() => getInitialEditConfigurationState(configurations));

  const handleCreateClick = async () => {
    setLoading(true);
    const [newConfiguration, error] = await createConfiguration({ name: formState.name.trim() });
    setLoading(false);

    if (error) {
      logError(error, "[CreateConfigurationDialog] handleCreateClick");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Configuration created successfully");
    onCreated(newConfiguration);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>New Configuration</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <TextField
          label="Name"
          value={formState.name}
          onChange={(e) => setFormState(updateNameAction(e.target.value))}
          error={!formState.validationResult.isValid}
          helperText={formState.validationResult.error}
          fullWidth
          autoFocus
        />
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          loading={loading}
          onClick={handleCreateClick}
          disabled={!formState.validationResult.isValid || !isFormDirty(formState)}
        >
          Create
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CreateConfigurationDialog;
